/**
 * What an access request means, kept out of the components so it can be tested
 * (design evaluation F17, F23, decision D6).
 *
 * - Whose request it is is decided by user id, never by display name: two people
 *   can share a name, and a renamed admin could approve their own request.
 * - The K8s scope is read from metadata.namespaces, where ['*'] is cluster-wide.
 * - The expiry an approver is shown is the concrete time access would end if
 *   approved now, not the requested duration restated as "remaining".
 * - The lifecycle is the path the request really took: an expired request that
 *   was never decided was not approved, and a revoked one ended early.
 */
import type { AccessRequest } from '@/types';

/**
 * Fields the server may send beyond src/types' AccessRequest. They are optional
 * because the API does not promise them yet; see the server follow-ups in the
 * phase report (requester id and email, expires_at, expired_at).
 */
export interface AccessRequestRecord extends Omit<AccessRequest, 'status'> {
  status: AccessRequest['status'] | 'rejected' | 'active' | 'provisioned' | (string & {});
  requested_by_id?: string;
  requester_id?: string;
  user_id?: string;
  requested_by_name?: string;
  requested_by_email?: string;
  requester_email?: string;
  expires_at?: string;
  expired_at?: string;
  rejected_by?: string;
  rejected_at?: string;
}

/** The parts of the signed-in user that identify them. */
export interface CurrentUserIdentity {
  subject?: string | null;
  email?: string | null;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

/** The requester's user id, if the server sent one. */
export function requesterIdOf(r: AccessRequestRecord): string | undefined {
  const m = (r.metadata ?? {}) as Record<string, unknown>;
  return str(r.requested_by_id) ?? str(r.requester_id) ?? str(r.user_id) ?? str(m.requester_id) ?? str(m.requested_by_id);
}

/** The requester's email, if the server sent one (or requested_by is one). */
export function requesterEmailOf(r: AccessRequestRecord): string | undefined {
  const m = (r.metadata ?? {}) as Record<string, unknown>;
  const byIsEmail = r.requested_by?.includes('@') ? r.requested_by : undefined;
  return str(r.requested_by_email) ?? str(r.requester_email) ?? str(m.requester_email) ?? byIsEmail;
}

/** The requester's display name. */
export function requesterNameOf(r: AccessRequestRecord): string {
  return str(r.requested_by_name) ?? str(r.requested_by) ?? 'Unknown requester';
}

/**
 * True when the signed-in user filed this request. Matches on the requester's
 * user id against the session subject; when the server sends no separate id,
 * requested_by is taken as the id (or email) it is stored as. A display name
 * never matches, so a shared or changed name cannot decide it.
 *
 * This only hides the buttons. The server must refuse an approve or deny from
 * the requester on its own.
 */
export function isOwnRequest(r: AccessRequestRecord, user: CurrentUserIdentity | null | undefined): boolean {
  const subject = str(user?.subject);
  const email = str(user?.email)?.toLowerCase();
  if (!subject && !email) return false;
  const id = requesterIdOf(r);
  if (id) return !!subject && id === subject;
  const by = str(r.requested_by);
  if (!by) return false;
  if (subject && by === subject) return true;
  return !!email && by.toLowerCase() === email;
}

/* ── K8s scope ─────────────────────────────────────────────────────────── */

export type K8sScope =
  | { kind: 'cluster' }
  | { kind: 'namespaces'; namespaces: string[] }
  | { kind: 'unspecified' };

/** The namespace scope of a K8s request; null when the request has none to show. */
export function k8sScopeOf(r: AccessRequestRecord): K8sScope | null {
  const raw = (r.metadata as Record<string, unknown> | undefined)?.namespaces;
  const list = Array.isArray(raw) ? raw.filter((n): n is string => typeof n === 'string' && n.trim() !== '') : [];
  if (list.includes('*')) return { kind: 'cluster' };
  if (list.length) return { kind: 'namespaces', namespaces: list };
  return r.resource_type === 'k8s' ? { kind: 'unspecified' } : null;
}

/** The scope in one line, for a confirm message. */
export function describeScope(scope: K8sScope | null): string | null {
  if (!scope) return null;
  if (scope.kind === 'cluster') return 'Cluster-wide (all namespaces)';
  if (scope.kind === 'namespaces') return `Namespaces: ${scope.namespaces.join(', ')}`;
  return 'No namespace scope recorded';
}

/* ── Duration and expiry ───────────────────────────────────────────────── */

export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds || 0));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts = [d && `${d}d`, h && `${h}h`, m && `${m}m`].filter(Boolean);
  return parts.length ? parts.join(' ') : '0m';
}

/** When access would end if it were approved at `now`. */
export function expiryIfApprovedNow(r: Pick<AccessRequestRecord, 'duration_seconds'>, now: number = Date.now()): Date {
  return new Date(now + Math.max(0, r.duration_seconds || 0) * 1000);
}

const toTime = (v: string | undefined): number | null => {
  if (!v) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/** When granted access ends (or ended): the server's expires_at, else approval + duration. */
export function grantedUntil(r: AccessRequestRecord): Date | null {
  const explicit = toTime(r.expires_at);
  if (explicit !== null) return new Date(explicit);
  const approved = toTime(r.approved_at);
  return approved === null ? null : new Date(approved + Math.max(0, r.duration_seconds || 0) * 1000);
}

/* ── Lifecycle ─────────────────────────────────────────────────────────── */

/** One request status word, with the server's synonyms folded together. */
export type LifecycleStatus = 'pending' | 'approved' | 'denied' | 'expired' | 'revoked' | 'unknown';

export function normaliseStatus(status: string | null | undefined): LifecycleStatus {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'pending': return 'pending';
    case 'approved': case 'active': case 'provisioned': return 'approved';
    case 'denied': case 'rejected': return 'denied';
    case 'expired': return 'expired';
    case 'revoked': return 'revoked';
    default: return 'unknown';
  }
}

/**
 * done: happened. current: under way now. upcoming: not reached yet.
 * ended: the access window closed (expired or revoked). failed: the request
 * did not get access (denied, or lapsed without a decision).
 */
export type StepKind = 'done' | 'current' | 'upcoming' | 'ended' | 'failed';

export interface LifecycleStep {
  key: 'submitted' | 'review' | 'approved' | 'denied' | 'active' | 'expired' | 'revoked' | 'lapsed';
  label: string;
  kind: StepKind;
  at?: string;
  /** Read before `at`, e.g. "Until" for an access window still open. */
  atPrefix?: string;
  by?: string;
  detail?: string;
}

/** True when the request reached an approval (by status or by its own record). */
function wasApproved(r: AccessRequestRecord, status: LifecycleStatus): boolean {
  return status === 'approved' || status === 'revoked' || !!r.approved_at || !!r.approved_by;
}

/** The path a request took, in order, as the timeline draws it. */
export function lifecycleSteps(r: AccessRequestRecord): LifecycleStep[] {
  const status = normaliseStatus(r.status);
  const submitted: LifecycleStep = { key: 'submitted', label: 'Submitted', kind: 'done', at: r.requested_at, by: requesterNameOf(r) };
  const approved: LifecycleStep = { key: 'approved', label: 'Approved', kind: 'done', at: r.approved_at, by: r.approved_by };
  const until = grantedUntil(r)?.toISOString();

  switch (status) {
    case 'pending':
      return [
        submitted,
        { key: 'review', label: 'Awaiting decision', kind: 'current' },
        { key: 'active', label: 'Access window', kind: 'upcoming', detail: `${formatDuration(r.duration_seconds)} from approval` },
      ];
    case 'approved':
      return [
        submitted,
        approved,
        { key: 'active', label: 'Access active', kind: 'current', at: until, atPrefix: until ? 'Until' : undefined },
      ];
    case 'denied':
      return [
        submitted,
        {
          key: 'denied', label: 'Denied', kind: 'failed',
          at: r.denied_at ?? r.rejected_at, by: r.denied_by ?? r.rejected_by,
          detail: r.denial_reason ? `Reason: ${r.denial_reason}` : undefined,
        },
      ];
    case 'expired':
      if (!wasApproved(r, status)) {
        return [
          submitted,
          { key: 'lapsed', label: 'Expired without a decision', kind: 'failed', at: r.expired_at, detail: 'Nobody approved or denied it in time; no access was granted.' },
        ];
      }
      return [
        submitted,
        approved,
        { key: 'active', label: 'Access granted', kind: 'done' },
        { key: 'expired', label: 'Expired', kind: 'ended', at: r.expired_at ?? until },
      ];
    case 'revoked':
      return [
        submitted,
        approved,
        { key: 'active', label: 'Access granted', kind: 'done' },
        { key: 'revoked', label: 'Revoked', kind: 'ended', at: r.revoked_at, by: r.revoked_by },
      ];
    default:
      return [submitted];
  }
}

/* ── Requested duration ────────────────────────────────────────────────── */

export const DURATION_PRESETS = [
  { label: '1 hour', seconds: 1 * 3600 },
  { label: '4 hours', seconds: 4 * 3600 },
  { label: '8 hours', seconds: 8 * 3600 },
  { label: '1 day', seconds: 24 * 3600 },
  { label: '1 week', seconds: 7 * 24 * 3600 },
] as const;

/**
 * The longest custom request, in hours. The client knows no per-policy JIT
 * maximum (no endpoint exposes one), so the ceiling is the longest preset; the
 * server stays the authority and may refuse a shorter maximum.
 */
export const MAX_CUSTOM_HOURS = 7 * 24;

/** Custom hours as seconds, or null when not a whole number from 1 to `max`. */
export function customHoursToSeconds(hours: number | null | undefined, max: number = MAX_CUSTOM_HOURS): number | null {
  if (hours === null || hours === undefined || !Number.isInteger(hours)) return null;
  if (hours < 1 || hours > max) return null;
  return hours * 3600;
}
